import React from 'react';
import { Box, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';

const clampNonNegative = (value: number): number => {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
};

interface MetadataSectionProps {
  label: string;
  description: string;
  priority: number;
  isActive: boolean;
  onLabelChange: (label: string) => void;
  onDescriptionChange: (description: string) => void;
  onPriorityChange: (priority: number) => void;
  onActiveChange: (isActive: boolean) => void;
  onLabelBlur?: () => void;
  labelError?: string;
  disabled?: boolean;
}

const MetadataSection: React.FC<MetadataSectionProps> = ({
  label,
  description,
  priority,
  isActive,
  onLabelChange,
  onDescriptionChange,
  onPriorityChange,
  onActiveChange,
  onLabelBlur,
  labelError,
  disabled = false,
}) => {
  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end' }}>
      <FormControlStyled sx={{ flex: 1, minWidth: 200, mt: 0 }}>
        <FormLabelStyled>
          LABEL <span style={{ color: '#d32f2f' }}>*</span>
        </FormLabelStyled>
        <TextField
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          onBlur={onLabelBlur}
          error={!!labelError}
          helperText={labelError}
          disabled={disabled}
          size="small"
          variant="outlined"
          placeholder="e.g. Exclude small non-PDF files"
          fullWidth
          sx={{ '& .MuiOutlinedInput-input': { fontSize: '13px', py: '8.5px' } }}
          data-cy="input-ruleset-label"
        />
      </FormControlStyled>

      <FormControlStyled sx={{ flex: 1, minWidth: 200, mt: 0 }}>
        <FormLabelStyled>DESCRIPTION</FormLabelStyled>
        <TextField
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={disabled}
          size="small"
          variant="outlined"
          placeholder="Optional"
          fullWidth
          sx={{ '& .MuiOutlinedInput-input': { fontSize: '13px', py: '8.5px' } }}
          data-cy="input-ruleset-description"
        />
      </FormControlStyled>

      <FormControlStyled sx={{ width: 100, mt: 0 }}>
        <FormLabelStyled sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          PRIORITY
          <Tooltip title="Lower = first" arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        </FormLabelStyled>
        <TextField
          type="number"
          value={priority}
          onChange={(e) => onPriorityChange(clampNonNegative(Number(e.target.value)))}
          disabled={disabled}
          size="small"
          variant="outlined"
          fullWidth
          inputProps={{ min: 0 }}
          sx={{ '& .MuiOutlinedInput-input': { fontSize: '13px', py: '8.5px' } }}
          data-cy="input-ruleset-priority"
        />
      </FormControlStyled>

      <Box
        onClick={() => !disabled && onActiveChange(!isActive)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: isActive ? '#66BBA9' : 'divider',
          backgroundColor: isActive ? 'rgba(102, 187, 169, 0.08)' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s',
          height: 37,
          '&:hover': {
            borderColor: disabled ? undefined : (isActive ? '#66BBA9' : '#999'),
          },
        }}
        data-cy="toggle-ruleset-active"
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: isActive ? '#66BBA9' : '#999',
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: isActive ? '#66BBA9' : 'text.secondary',
            fontSize: '13px',
          }}
        >
          {isActive ? 'Active' : 'Inactive'}
        </Typography>
      </Box>
    </Box>
  );
};

export default MetadataSection;
